// Authorization to Communicate (LOA) actions on the deal detail Claims &
// Insurance section. May 7, 2026.
//
// Renders below the adjuster fields when deals.communication_authorization_pdf_url
// is populated. Lets staff:
//   1. View the signed LOA PDF in a new tab
//   2. Open a pre-filled mailto: to send the LOA to a carrier claims address
//
// The PDF itself is also visible in the deal's Documents tab (it's inserted
// into deal_documents at signing time) so this is just a quick-access shelf.

import type { Deal } from "../../lib/types";

export default function CommunicationAuthorizationActions({ deal }: { deal: Deal }) {
  const pdfUrl = deal.communication_authorization_pdf_url;
  if (!pdfUrl) return null;

  const signedAt = deal.communication_authorization_signed_at
    ? new Date(deal.communication_authorization_signed_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  // Pre-filled email body. Shannon's the primary user; she'll attach the PDF
  // to the email manually after the mailto: opens (mailto: doesn't support
  // attachments). The body explicitly references the PDF + claim number so
  // the carrier rep knows what they're getting.
  const subject = `Authorization to Communicate — ${deal.homeowner_name ?? ""} (Claim ${deal.claim_number ?? ""})`;
  const body =
    `Hello,\n\n` +
    `Please find attached the signed Authorization to Communicate from our mutual ` +
    `policyholder ${deal.homeowner_name ?? ""} (Claim ${deal.claim_number ?? ""}).\n\n` +
    `This authorizes Bardin Roofing to discuss this claim, request scope and ` +
    `correspondence, submit supplements, and attend inspections on the policyholder's behalf.\n\n` +
    `Please cc claims@bardinroofing.com on all related correspondence going forward.\n\n` +
    `Thank you,\n` +
    `Bardin Roofing — Claims Coordination\n` +
    `(470) 823-8816  |  claims@bardinroofing.com`;
  const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-base">📄</span>
            <span className="text-sm font-semibold text-emerald-900">
              Authorization to Communicate on file
            </span>
          </div>
          <div className="text-xs text-emerald-800/80 mt-0.5">
            Signed by {deal.homeowner_name ?? "the homeowner"}
            {signedAt ? ` on ${signedAt}` : ""}. Send this PDF to the insurance
            claims department to authorize Bardin to discuss the claim.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-100 transition"
          >
            👁 View
          </a>
          <a
            href={mailto}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition"
            title="Opens your email client with a pre-filled message — attach the LOA PDF before sending."
          >
            ✉ Send to insurance
          </a>
        </div>
      </div>
      <div className="text-[11px] text-emerald-800/70 mt-2">
        ⓘ Email opens with a pre-filled body. Download the PDF first (View → Save), then attach it to the email before sending.
      </div>
    </div>
  );
}
